/**
 * All site copy lives here. Edit this file to update the site — the
 * components render whatever they find, so adding, removing, or reordering
 * entries needs no other changes.
 *
 * Notes:
 * - Wrap text in **double asterisks** to bold it inside a paragraph.
 * - Any section left empty (no paragraphs, no entries, no items) disappears
 *   from the page and from the nav automatically.
 * - `id` is the scroll anchor and `navLabel` is what the header shows.
 */

const content = {
  meta: {
    name: 'Andre Cardoso',
    initials: 'AC',
    // Browser tab title and the description search engines show.
    title: 'Andre Cardoso',
    description: 'Andre Cardoso — Software Engineer & Developer',
  },

  hero: {
    greeting: "Hi, I'm",
    // One entry renders as static text. Several entries cycle as a typewriter.
    // Delete all but one when you're ready to drop the typing effect.
    roles: [
      'Engineer',
      'Developer',
      'Designer',
      'Builder',
    ],
    tagline: 'Building thoughtful software & digital experiences.',
    primaryCta: { label: 'View My Work', target: 'projectsSection' },
    secondaryCta: { label: 'Get In Touch', target: 'contact' },
  },

  about: {
    id: 'aboutMESection',
    navLabel: 'About',
    label: 'About Me',
    heading: 'Who I Am',
    paragraphs: [
      "I'm **Andre Cardoso**, a software engineer with a foundation in Computer Engineering from Northeastern University. I thrive at the intersection of thoughtful engineering and real-world impact — whether that's automating CI/CD pipelines, supporting enterprise customers at Cisco, or building community tools used by thousands of people daily.",
      "Born in Lisbon, Portugal, raised in New York City, and educated in Boston — I bring a global perspective and a diverse skill set to everything I build. I'm currently on sabbatical, exploring what's next and open to exciting opportunities.",
      "Beyond code, I've led teams as a stage manager in theater, honing the organizational and communication skills that make great engineers great teammates.",
    ],
    // Add or remove whole groups freely; each renders as a titled list of chips.
    lists: [
      {
        title: 'Technical Skills',
        items: ['Python', 'JavaScript', 'React', 'Docker', 'CI/CD', 'Linux'],
      },
      {
        title: 'Languages',
        items: ['English', 'Portuguese', 'Spanish'],
      },
    ],
  },

  experience: {
    id: 'experienceSection',
    navLabel: 'Experience',
    label: 'Experience',
    heading: "Where I've Been",
    entries: [
      {
        role: 'TAC Engineer',
        company: 'Cisco',
        period: '2023 – 2023',
        type: 'Work',
        description:
          'Delivered technical support on the Webex TAC team, resolving complex enterprise issues and improving customer satisfaction metrics. Gained deep expertise in enterprise collaboration infrastructure and network troubleshooting.',
      },
      {
        role: 'CI/CD Developer',
        company: 'Vodafone',
        period: '2024 – 2025',
        type: 'Work',
        description:
          'Automated code integration and deployment pipelines, significantly improving build reliability and reducing deployment time. Contributed to the shift toward continuous delivery best practices across engineering teams.',
      },
      {
        role: 'Software Engineering',
        company: 'University of Miami',
        period: '2025 – 2028',
        type: 'Education',
        description:
          'Studied software Engineering with coursework in algorithms, and software development',
      },
      {
        role: 'Computer Engineering',
        company: 'Northeastern University',
        period: '2020 – 2023',
        type: 'Education',
        description:
          'Studied Computer Engineering with coursework in algorithms, systems programming, and software development. Participated in co-op programs that bridged academic learning with industry experience.',
      },
      {
        role: 'Stage Manager',
        company: 'Theater Production',
        period: '2019 – 202',
        type: 'Leadership',
        description:
          'Led production teams of 15+ people, coordinating schedules, technical cues, and rehearsals. Developed strong organizational, communication, and leadership skills under high-pressure live environments.',
      },
    ],
  },

  projects: {
    id: 'projectsSection',
    navLabel: 'Projects',
    label: 'Work',
    heading: 'Projects & Websites',
    // Filter tabs are built from the `category` values below and only appear
    // when there is more than one distinct category.
    allTabLabel: 'All',
    items: [
      {
        category: 'Project',
        name: 'Twitter Wordle Bot',
        description:
          'Developed a Twitter bot that analyzes Wordle-related tweets to calculate average guesses per day and track letter frequency distributions. Built a custom algorithm to attempt solving the daily Wordle and benchmarks it against real user performance.',
        tags: ['Python', 'Twitter API', 'Data Analysis', 'NLP'],
        link: 'https://github.com/Acardoso21',
        linkLabel: 'GitHub',
      },
      {
        category: 'Website',
        name: 'Voron3D.wiki',
        description:
          'Deployed and maintained a self-hosted Wiki.js instance for the Voron3D 3D-printing community. Rapidly grew to nearly 10k daily active users. Implemented secure authentication, analytics dashboards, and structured content management.',
        tags: ['Wiki.js', 'Self-hosted', 'Analytics', 'Community', 'Linux'],
        link: 'https://voron3d.wiki',
        linkLabel: 'Visit Site',
      },
      {
        category: 'Project',
        name: 'TicTacToe AI',
        description:
          'One of my first personal projects — a Python Tic-Tac-Toe game built during the COVID-19 pandemic. Implemented a computer opponent with basic game-tree logic, which laid the foundation for my understanding of algorithms and game AI.',
        tags: ['Python', 'Game Logic', 'AI', 'Algorithms'],
        link: 'https://github.com/Acardoso21/TicTacToe/blob/main/tiktactoe_V2.py',
        linkLabel: 'GitHub',
      },
    ],
  },

  contact: {
    id: 'contact',
    navLabel: 'Contact',
    label: 'Contact',
    heading: 'Get In Touch',
    intro:
      "Have a project in mind, a question, or just want to say hi? I'd love to hear from you.",
    // Add or remove fields freely. `type` is 'text', 'email', or 'textarea'.
    fields: [
      { name: 'name', label: 'Name', type: 'text', placeholder: 'Your name', required: true },
      { name: 'email', label: 'Email', type: 'email', placeholder: 'your@email.com', required: true },
      { name: 'message', label: 'Message', type: 'textarea', placeholder: "What's on your mind?", required: true },
    ],
    submitLabel: 'Send Message',
    successMessage: "Thank you for reaching out!\nI'll get back to you soon.",
    errorMessage:
      'There seems to be a problem with the form. Please email me directly.',
    email: 'contactme@andrecardoso.me',
  },

  footer: {
    copyright: '© 2025 Andre Cardoso. All rights reserved.',
    // `icon` is one of: 'email', 'linkedin', 'github'.
    links: [
      { label: 'Email', icon: 'email', href: 'mailto:contactme@andrecardoso.me' },
      { label: 'LinkedIn', icon: 'linkedin', href: 'https://www.linkedin.com/in/andre-cardoso-/' },
      { label: 'GitHub', icon: 'github', href: 'https://github.com/AMOVFC' },
    ],
  },
};

export default content;
